import type { SessionPricePoint } from '@/types/session';
import { formatPrice } from './dashboardFormat';
import {
  SESSION_LENGTH_MINUTES,
  SESSION_TIME_TICKS,
  pricePathExtent,
  sessionMinutes,
} from './railCharts.utils';

interface SessionPricePathChartProps {
  path: readonly SessionPricePoint[];
  currentPrice: number | null;
  width: number;
}

const HEIGHT = 140;
const TOP = 8;
const BOTTOM = 26;
const LEFT = 40;
const RIGHT = 62;

export function SessionPricePathChart({ path, currentPrice, width }: SessionPricePathChartProps) {
  const w = Math.max(240, width);
  const { step, lo, hi } = pricePathExtent(path, currentPrice);
  const y = (price: number) => TOP + (1 - (price - lo) / (hi - lo || 1)) * (HEIGHT - TOP - BOTTOM);
  const x = (minutes: number) => LEFT + (minutes / SESSION_LENGTH_MINUTES) * (w - LEFT - RIGHT);
  const gridCount = Math.round((hi - lo) / step);
  const labelEvery = gridCount > 7 ? 2 : 1;
  const gridLines = Array.from({ length: Math.floor(gridCount / labelEvery) + 1 }, (_, index) =>
    Number((lo + index * labelEvery * step).toFixed(4)),
  );
  const points = path
    .flatMap((point) => {
      const minutes = sessionMinutes(point.timestamp);
      return minutes == null ? [] : [`${x(minutes).toFixed(1)},${y(point.price).toFixed(1)}`];
    })
    .join(' ');
  const currentY = currentPrice == null ? null : y(currentPrice);

  return (
    <svg
      role="img"
      aria-label="Session price path"
      width={w}
      height={HEIGHT}
      viewBox={`0 0 ${w} ${HEIGHT}`}
      className="block w-full font-sans text-xs [&_text]:fill-soft"
    >
      {gridLines.map((price) => (
        <g key={price}>
          <line
            x1={LEFT}
            x2={w - RIGHT}
            y1={y(price)}
            y2={y(price)}
            stroke="rgba(255,255,255,.05)"
          />
          <text x={LEFT - 8} y={y(price) + 4} textAnchor="end" className="font-mono">
            {price}
          </text>
        </g>
      ))}
      {SESSION_TIME_TICKS.map((tick) => (
        <text key={tick.label} x={x(tick.minutes)} y={HEIGHT - BOTTOM + 20} textAnchor="middle">
          {tick.label}
        </text>
      ))}
      <line x1={LEFT} x2={LEFT} y1={TOP} y2={HEIGHT - BOTTOM} stroke="rgba(255,255,255,.25)" />
      <line
        x1={LEFT}
        x2={w - RIGHT}
        y1={HEIGHT - BOTTOM}
        y2={HEIGHT - BOTTOM}
        stroke="rgba(255,255,255,.25)"
      />
      <polyline
        fill="none"
        stroke="#22c55e"
        strokeWidth={1.6}
        strokeLinejoin="round"
        points={points}
      />
      {currentY != null && (
        <g>
          <line
            x1={LEFT}
            x2={w - RIGHT + 4}
            y1={currentY}
            y2={currentY}
            stroke="#facc15"
            strokeWidth={1.5}
            strokeDasharray="5 5"
          />
          <rect
            x={w - RIGHT + 6}
            y={currentY - 11}
            width={RIGHT - 8}
            height={22}
            rx={3}
            fill="#0a0a0a"
            stroke="#facc15"
          />
          <text
            x={w - RIGHT + 6 + (RIGHT - 8) / 2}
            y={currentY + 4}
            textAnchor="middle"
            className="font-mono"
            style={{ fill: '#facc15' }}
          >
            {formatPrice(currentPrice)}
          </text>
        </g>
      )}
    </svg>
  );
}
